// AU17 (docs/build-briefs/AU17-change-history.md; design/mockups/au17-*.html; DEC-309). The Change
// history screen, drawn inside the operations frame ui/src/admin.ts owns (its header names the screen
// and "Menu" goes back). This module owns what is inside: the filters, the list, the detail sheet and
// the CSV download.
//
// Admin surface: a person on a computer or a tablet. Every control is 44px or taller and nothing
// depends on hover. Text is set with textContent only (names and values are user entered). Nothing here
// can change a record: it reads, and it can write nothing.
//
// States: loading, the list, empty (first use and filtered), error with Try again, and no access (a
// 403, which is also what a person whose permission was just taken away sees).

import { SERVICE_UNAVAILABLE_SENTENCE } from "./serviceUnavailable.ts";
import {
  getChangeHistory,
  getChangeHistoryCsv,
  type ChangeHistoryChangeDto,
  type ChangeHistoryEntryDto,
  type ChangeHistoryPageDto,
} from "./api.ts";
import {
  CHANGE_HISTORY_RANGE_CHIPS,
  changeHistoryQuery,
  countLine,
  defaultFilters,
  emptyCopy,
  filterPills,
  filtersActive,
  groupByDay,
  historyStartsText,
  markParts,
  panelListHeading,
  shortDate,
  showingLine,
  type ChangeHistoryFilters,
  type ChangeHistoryRangeId,
} from "./changeHistoryModel.ts";
import { createExportMenu } from "./exportMenu.ts";
import { deliverExportFile } from "./googleSheets.ts";
import type { GateExportFormat } from "./exportMenuModel.ts";
import { capitalizeServerMessage } from "./serverMessageDisplay.ts";
import { hasPermission, type Session } from "./session.ts";
import { sortOptions } from "./nameSort.ts";

const SVG_NS = "http://www.w3.org/2000/svg";

type Status = "idle" | "loading" | "ready" | "error" | "no-access";

interface State {
  session: Session | undefined;
  filters: ChangeHistoryFilters;
  status: Status;
  errorMessage: string;
  /** The first page's answer, kept for the filter lists, the zone and the footer. */
  page: ChangeHistoryPageDto | undefined;
  entries: ChangeHistoryEntryDto[];
  nextCursor: string | null;
  total: number | null;
  loadingMore: boolean;
  openEntryId: string | undefined;
  /** Bumped on every load and on close, so an answer that arrives late is ignored. */
  generation: number;
  searchTimer: ReturnType<typeof setTimeout> | undefined;
  downloadNote: string;
}

const state: State = {
  session: undefined,
  filters: defaultFilters(),
  status: "idle",
  errorMessage: "",
  page: undefined,
  entries: [],
  nextCursor: null,
  total: null,
  loadingMore: false,
  openEntryId: undefined,
  generation: 0,
  searchTimer: undefined,
  downloadNote: "",
};

interface Parts {
  root: HTMLElement;
  filtersCard: HTMLElement;
  downloadButton: HTMLButtonElement;
  downloadNote: HTMLElement;
  chipRow: HTMLElement;
  customRow: HTMLElement;
  fromInput: HTMLInputElement;
  toInput: HTMLInputElement;
  personSelect: HTMLSelectElement;
  areaSelect: HTMLSelectElement;
  searchInput: HTMLInputElement;
  pillBar: HTMLElement;
  summary: HTMLElement;
  results: HTMLElement;
  scrim: HTMLElement;
  panel: HTMLElement;
  panelBody: HTMLElement;
  panelEyebrow: HTMLElement;
  panelTitle: HTMLElement;
  closeButton: HTMLButtonElement;
  doneButton: HTMLButtonElement;
}

let parts: Parts | undefined;
/** The row that opened the sheet, so closing it returns focus there. */
let returnFocusTo: HTMLElement | undefined;

// ---------------------------------------------------------------------------------------------
// Small DOM helpers (text only)
// ---------------------------------------------------------------------------------------------

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  testId?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (className !== undefined && className !== "") element.className = className;
  if (testId !== undefined) element.dataset["testid"] = testId;
  if (text !== undefined) element.textContent = text;
  return element;
}

function icon(kind: "lock" | "download" | "chevron" | "close" | "search"): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  const path = document.createElementNS(SVG_NS, "path");
  switch (kind) {
    case "lock": {
      svg.setAttribute("viewBox", "0 0 20 20");
      svg.setAttribute("class", "ch-ico");
      path.setAttribute("d", "M5 9V6.5a5 5 0 0 1 10 0V9M4 9h12v8H4z");
      break;
    }
    case "download": {
      svg.setAttribute("viewBox", "0 0 20 20");
      svg.setAttribute("class", "ch-ico");
      path.setAttribute("d", "M10 3v10m0 0-4-4m4 4 4-4M4 17h12");
      break;
    }
    case "chevron": {
      svg.setAttribute("viewBox", "0 0 10 16");
      svg.setAttribute("class", "ch-chev");
      path.setAttribute("d", "M2 2l6 6-6 6");
      break;
    }
    case "close": {
      svg.setAttribute("viewBox", "0 0 20 20");
      svg.setAttribute("class", "ch-ico");
      path.setAttribute("d", "M5 5l10 10M15 5L5 15");
      break;
    }
    case "search": {
      svg.setAttribute("viewBox", "0 0 20 20");
      svg.setAttribute("class", "ch-ico");
      path.setAttribute("d", "M8.5 3a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11zm4 9.5L17 17");
      break;
    }
  }
  svg.append(path);
  return svg;
}

function markedSpan(text: string, className: string, q: string): HTMLElement {
  const span = el("span", className);
  for (const piece of markParts(text, q)) {
    if (piece.marked) {
      const mark = document.createElement("mark");
      mark.textContent = piece.text;
      span.append(mark);
    } else {
      span.append(document.createTextNode(piece.text));
    }
  }
  return span;
}

function areaTagClass(areaId: string): string {
  if (areaId === "people_access") return "ch-tag ch-tag-access";
  if (areaId === "legal") return "ch-tag ch-tag-legal";
  return "ch-tag";
}

// ---------------------------------------------------------------------------------------------
// Building the fixed parts, once
// ---------------------------------------------------------------------------------------------

function buildOnce(container: HTMLElement): Parts {
  container.textContent = "";
  const root = el("div", "ch-root", "change-history-root");

  const downloadButton = el("button", "ch-btn-secondary", "change-history-download-csv");
  downloadButton.type = "button";
  downloadButton.append(icon("download"), document.createTextNode("Export"));
  const downloadNote = el("p", "ch-download-note", "change-history-download-note");
  downloadNote.setAttribute("role", "status");

  const filters = el("div", "ch-card ch-filters", "change-history-filters");
  const chipRow = el("div", "ch-rangebar");
  chipRow.setAttribute("role", "group");
  chipRow.setAttribute("aria-label", "Date range");
  for (const chip of CHANGE_HISTORY_RANGE_CHIPS) {
    const button = el("button", "ch-chip", `change-history-chip-${chip.id}`, chip.label);
    button.type = "button";
    button.dataset["range"] = chip.id;
    button.addEventListener("click", () => handleRangeChip(chip.id));
    chipRow.append(button);
  }
  // Download CSV sits at the end of the chip row, as Sales by day puts it at the end of its own.
  const rowActions = el("div", "ch-rangebar-actions");
  rowActions.append(downloadNote, downloadButton);
  // The menu wraps the button where it now sits, so it is built after the button is placed.
  createExportMenu({
    trigger: downloadButton,
    testIdPrefix: "change-history-export",
    menuActive: () => true,
    googleSheet: () => state.session,
    onPick: (format) => void handleDownload(format),
    onPlain: () => void handleDownload("csv"),
  });
  chipRow.append(rowActions);

  const customRow = el("div", "ch-custom-row", "change-history-custom-row");
  customRow.hidden = true;
  const fromField = el("div", "ch-fld");
  const fromLabel = el("label", "", undefined, "From");
  const fromInput = el("input", "ch-txt ch-date", "change-history-from");
  fromInput.type = "date";
  fromInput.id = "change-history-from";
  fromLabel.htmlFor = fromInput.id;
  fromField.append(fromLabel, fromInput);
  const toField = el("div", "ch-fld");
  const toLabel = el("label", "", undefined, "To");
  const toInput = el("input", "ch-txt ch-date", "change-history-to");
  toInput.type = "date";
  toInput.id = "change-history-to";
  toLabel.htmlFor = toInput.id;
  toField.append(toLabel, toInput);
  customRow.append(fromField, toField);
  const onCustomChange = (): void => {
    state.filters.from = fromInput.value;
    state.filters.to = toInput.value;
    if (fromInput.value !== "" && toInput.value !== "") void load();
  };
  fromInput.addEventListener("change", onCustomChange);
  toInput.addEventListener("change", onCustomChange);

  const pickRow = el("div", "ch-pickrow");
  const personField = el("div", "ch-fld");
  const personLabel = el("label", "", undefined, "Person");
  const personSelect = el("select", "ch-sel", "change-history-person");
  personSelect.id = "change-history-person";
  personLabel.htmlFor = personSelect.id;
  personSelect.addEventListener("change", () => {
    state.filters.person = personSelect.value;
    void load();
  });
  personField.append(personLabel, personSelect);
  const areaField = el("div", "ch-fld");
  const areaLabelEl = el("label", "", undefined, "Area");
  const areaSelect = el("select", "ch-sel", "change-history-area");
  areaSelect.id = "change-history-area";
  areaLabelEl.htmlFor = areaSelect.id;
  areaSelect.addEventListener("change", () => {
    state.filters.area = areaSelect.value;
    void load();
  });
  areaField.append(areaLabelEl, areaSelect);
  const searchField = el("div", "ch-fld ch-fld-search");
  const searchLabel = el("label", "", undefined, "Search (optional)");
  const searchInput = el("input", "ch-txt ch-search", "change-history-search");
  searchInput.id = "change-history-search";
  searchInput.type = "text";
  searchInput.placeholder = "Search what changed";
  searchInput.maxLength = 100;
  searchInput.setAttribute("inputmode", "search");
  searchInput.setAttribute("autocomplete", "off");
  searchLabel.htmlFor = searchInput.id;
  searchInput.addEventListener("input", () => {
    if (state.searchTimer !== undefined) clearTimeout(state.searchTimer);
    state.searchTimer = setTimeout(() => {
      state.searchTimer = undefined;
      state.filters.q = searchInput.value;
      void load();
    }, 350);
  });
  searchInput.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      if (state.searchTimer !== undefined) clearTimeout(state.searchTimer);
      state.searchTimer = undefined;
      state.filters.q = searchInput.value;
      void load();
    }
  });
  searchField.append(searchLabel, searchInput);
  pickRow.append(personField, areaField, searchField);

  const pillBar = el("div", "ch-activebar", "change-history-pills");
  filters.append(chipRow, customRow, pickRow, pillBar);

  const summary = el("div", "ch-summary", "change-history-summary");
  const results = el("div", "ch-card ch-results", "change-history-results");

  // The detail sheet: a side sheet at 1000px and wider, a bottom sheet below (the CSS decides).
  const scrim = el("div", "ch-scrim", "change-history-scrim");
  scrim.hidden = true;
  scrim.addEventListener("click", () => closeDetail());
  const panel = el("aside", "ch-panel", "change-history-detail");
  panel.hidden = true;
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-modal", "true");
  panel.setAttribute("aria-labelledby", "change-history-detail-title");
  panel.append(el("div", "ch-grab"));
  const head = el("div", "ch-ph");
  const headText = el("div", "ch-ph-text");
  const panelEyebrow = el("div", "ch-ey", "change-history-detail-area");
  const panelTitle = el("h2", "", "change-history-detail-title");
  panelTitle.id = "change-history-detail-title";
  headText.append(panelEyebrow, panelTitle);
  const closeButton = el("button", "ch-xbtn", "change-history-detail-close");
  closeButton.type = "button";
  closeButton.setAttribute("aria-label", "Close");
  closeButton.append(icon("close"));
  closeButton.addEventListener("click", () => closeDetail());
  head.append(headText, closeButton);
  const panelBody = el("div", "ch-pbody", "change-history-detail-body");
  const foot = el("div", "ch-pfoot");
  const doneButton = el(
    "button",
    "ch-btn-secondary ch-btn-wide",
    "change-history-detail-done",
    "Done",
  );
  doneButton.type = "button";
  doneButton.addEventListener("click", () => closeDetail());
  const lockLine = el("div", "ch-lockline");
  lockLine.append(
    icon("lock"),
    document.createTextNode("This record cannot be edited or deleted."),
  );
  foot.append(doneButton, lockLine);
  panel.append(head, panelBody, foot);
  panel.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeDetail();
  });

  root.append(filters, summary, results, scrim, panel);
  container.append(root);
  return {
    root,
    filtersCard: filters,
    downloadButton,
    downloadNote,
    chipRow,
    customRow,
    fromInput,
    toInput,
    personSelect,
    areaSelect,
    searchInput,
    pillBar,
    summary,
    results,
    scrim,
    panel,
    panelBody,
    panelEyebrow,
    panelTitle,
    closeButton,
    doneButton,
  };
}

// ---------------------------------------------------------------------------------------------
// Filter handlers
// ---------------------------------------------------------------------------------------------

function handleRangeChip(range: ChangeHistoryRangeId): void {
  state.filters.range = range;
  if (range !== "custom") {
    state.filters.from = "";
    state.filters.to = "";
    void load();
    return;
  }
  // Custom dates: start from the range just shown, then wait for both dates.
  if (state.page !== undefined) {
    state.filters.from = state.page.from;
    state.filters.to = state.page.to;
  }
  syncFilterControls();
  if (state.filters.from !== "" && state.filters.to !== "") void load();
}

function clearFilters(): void {
  state.filters.person = "";
  state.filters.area = "";
  state.filters.q = "";
  if (parts !== undefined) parts.searchInput.value = "";
  void load();
}

function removePill(kind: "person" | "area" | "q"): void {
  state.filters[kind] = "";
  if (kind === "q" && parts !== undefined) parts.searchInput.value = "";
  void load();
}

// ---------------------------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------------------------

function syncFilterControls(): void {
  if (parts === undefined) return;
  const p = parts;
  for (const button of Array.from(
    p.chipRow.querySelectorAll<HTMLButtonElement>("button.ch-chip"),
  )) {
    const on = button.dataset["range"] === state.filters.range;
    button.classList.toggle("ch-on", on);
    button.setAttribute("aria-pressed", on ? "true" : "false");
  }
  p.customRow.hidden = state.filters.range !== "custom";
  p.fromInput.value = state.filters.from;
  p.toInput.value = state.filters.to;
  const page = state.page;
  // The two lists come from the first answer and are rebuilt only when it changes.
  if (page !== undefined) {
    fillSelect(
      p.personSelect,
      // UX88 (DEC-339): people and areas read A to Z; "Everyone" and "All areas" stay first.
      sortOptions(
        [
          { value: "", label: "Everyone" },
          ...page.people.map((x) => ({ value: x.key, label: x.name })),
        ],
        (option) => option.value === "",
      ),
      state.filters.person,
    );
    fillSelect(
      p.areaSelect,
      sortOptions(
        [
          { value: "", label: "All areas" },
          ...page.areas.map((x) => ({ value: x.id, label: x.label })),
        ],
        (option) => option.value === "",
      ),
      state.filters.area,
    );
  }
  p.personSelect.classList.toggle("ch-on", state.filters.person !== "");
  p.areaSelect.classList.toggle("ch-on", state.filters.area !== "");
  p.searchInput.classList.toggle("ch-on", state.filters.q.trim() !== "");
  if (document.activeElement !== p.searchInput && p.searchInput.value !== state.filters.q) {
    p.searchInput.value = state.filters.q;
  }
  renderPills();
  // Only a list that loaded can be downloaded; with no access the whole filter card goes.
  p.downloadButton.disabled = state.status !== "ready";
  // EXP1-S4: a file needs report.export as well as the view permission, so the button is not offered without it.
  p.downloadButton.hidden =
    state.session === undefined || !hasPermission(state.session, "report.export");
  p.filtersCard.hidden = state.status === "no-access";
}

function fillSelect(
  select: HTMLSelectElement,
  options: readonly { value: string; label: string }[],
  chosen: string,
): void {
  const signature = options.map((o) => `${o.value}\u0000${o.label}`).join("\u0001");
  if (select.dataset["signature"] !== signature) {
    select.textContent = "";
    for (const option of options) {
      const node = document.createElement("option");
      node.value = option.value;
      node.textContent = option.label;
      select.append(node);
    }
    select.dataset["signature"] = signature;
  }
  // A chosen person who made no change in the new range is kept in the list so the filter reads true.
  if (chosen !== "" && !options.some((o) => o.value === chosen)) {
    const node = document.createElement("option");
    node.value = chosen;
    node.textContent = "Someone";
    select.append(node);
  }
  select.value = chosen;
}

function renderPills(): void {
  if (parts === undefined) return;
  const page = state.page;
  parts.pillBar.textContent = "";
  const pills = filterPills(state.filters, page?.people ?? [], page?.areas ?? []);
  parts.pillBar.hidden = pills.length === 0;
  for (const pill of pills) {
    const node = el("span", "ch-apill", `change-history-pill-${pill.kind}`);
    node.append(document.createTextNode(pill.text));
    const remove = el("button", "ch-apill-x", `change-history-pill-${pill.kind}-remove`);
    remove.type = "button";
    remove.setAttribute("aria-label", `Remove filter ${pill.text}`);
    remove.append(icon("close"));
    remove.addEventListener("click", () => removePill(pill.kind));
    node.append(remove);
    parts.pillBar.append(node);
  }
  if (pills.length > 0) {
    const clear = el("button", "ch-btn-quiet", "change-history-clear-filters", "Clear all");
    clear.type = "button";
    clear.addEventListener("click", () => clearFilters());
    parts.pillBar.append(clear);
  }
}

function renderSummary(): void {
  if (parts === undefined) return;
  parts.summary.textContent = "";
  if (state.status !== "ready" || state.page === undefined) return;
  const line = countLine(state.total, state.filters, state.page);
  const left = el("span", "ch-summary-left");
  const count = el("span", "", "change-history-count");
  if (line.count !== "")
    count.append(el("b", "", undefined, line.count), document.createTextNode(" "));
  count.append(document.createTextNode(line.rest));
  // The range, and the time zone every time on this screen is written in.
  const range = el("span", "ch-range-span", "change-history-range-span");
  range.append(
    el("b", "", undefined, shortDate(state.page.from)),
    document.createTextNode(" to "),
    el("b", "", undefined, shortDate(state.page.to)),
  );
  if (state.page.timeZoneName !== "" && state.page.timeZoneName !== "UTC") {
    range.append(document.createTextNode(` \u00b7 ${state.page.timeZoneName}`));
  }
  left.append(count, range);
  parts.summary.append(left, el("span", "", undefined, "Newest first"));
}

function renderHeadline(entry: ChangeHistoryEntryDto, q: string): HTMLElement {
  const h = entry.headline;
  const wrap = el("span", "ch-what-text");
  if (h.before !== "") wrap.append(markedSpan(h.before, "", q));
  if (h.subject !== null && h.subject !== "") wrap.append(markedSpan(h.subject, "ch-b", q));
  if (h.mid !== "") wrap.append(markedSpan(h.mid, "", q));
  if (h.was !== null && h.now !== null) {
    wrap.append(
      markedSpan(h.was, "ch-was", q),
      document.createTextNode(" to "),
      markedSpan(h.now, "ch-b", q),
    );
  } else if (h.tail !== null) {
    const tail = el("span", h.locked ? "ch-hid" : "");
    if (h.locked) tail.append(icon("lock"));
    const countMatch = /^(\d+)(\s.*)$/.exec(h.tail);
    if (!h.locked && countMatch !== null) {
      tail.append(
        el("b", "", undefined, countMatch[1]),
        document.createTextNode(countMatch[2] ?? ""),
      );
    } else {
      tail.append(markedSpan(h.tail, "", q));
    }
    wrap.append(tail);
  }
  if (entry.moreCount > 0) {
    wrap.append(el("span", "ch-more", undefined, `and ${entry.moreCount} more`));
  }
  return wrap;
}

function avatar(entry: ChangeHistoryEntryDto, small: boolean): HTMLElement {
  return el(
    "span",
    `ch-avatar${small ? " ch-avatar-sm" : ""}${entry.actor.kind === "user" ? "" : " ch-avatar-gray"}`,
    undefined,
    entry.actor.initials,
  );
}

function renderRow(entry: ChangeHistoryEntryDto, q: string): HTMLElement {
  const row = el("button", "ch-row", "change-history-row");
  row.type = "button";
  row.dataset["entryId"] = entry.id;
  row.dataset["area"] = entry.area.id;
  if (entry.id === state.openEntryId) row.classList.add("ch-row-sel");

  const when = el("span", "ch-when", undefined, entry.timeLabel);
  const who = el("span", "ch-who");
  who.append(avatar(entry, true), el("span", "ch-nm", undefined, entry.actor.name));
  const areaCell = el("span", "ch-areacell");
  areaCell.append(el("span", areaTagClass(entry.area.id), undefined, entry.area.label));
  const what = el("span", "ch-what");
  what.append(renderHeadline(entry, q));
  // The narrow layout shows When and Who on one line above the sentence.
  const mtop = el("span", "ch-mtop");
  mtop.append(
    el("b", "", undefined, entry.timeLabel),
    avatar(entry, true),
    document.createTextNode(entry.actor.name),
  );
  row.append(when, who, areaCell, mtop, what, icon("chevron"));
  row.addEventListener("click", () => openDetail(entry.id, row));
  return row;
}

function renderResults(): void {
  if (parts === undefined) return;
  const p = parts;
  p.results.textContent = "";
  p.results.classList.remove("ch-results-empty");

  if (state.status === "loading") {
    const note = el("div", "ch-loadnote", "change-history-loading");
    note.setAttribute("role", "status");
    note.append(
      el("span", "ch-spin"),
      document.createTextNode("Loading changes. A long date range can take a few seconds."),
    );
    p.results.append(headRow(), note);
    for (let i = 0; i < 5; i += 1) {
      const skel = el("div", "ch-skel");
      for (const cls of ["ch-bar ch-w3", "ch-bar ch-w4", "ch-bar ch-w1", "ch-bar ch-w2"]) {
        skel.append(el("span", cls));
      }
      p.results.append(skel);
    }
    return;
  }

  if (state.status === "no-access") {
    p.results.classList.add("ch-results-empty");
    const box = el("div", "ch-empty", "change-history-no-access");
    const badge = el("div", "ch-badge");
    badge.append(icon("lock"));
    box.append(
      badge,
      el("h2", "", undefined, "You do not have access to Change history"),
      el(
        "p",
        "",
        undefined,
        'This screen is turned off for you. Ask an admin to turn on "See the change history" for you.',
      ),
    );
    p.results.append(box);
    return;
  }

  if (state.status === "error") {
    p.results.classList.add("ch-results-empty");
    const box = el("div", "ch-empty", "change-history-error");
    box.setAttribute("role", "alert");
    box.append(
      el("h2", "", undefined, "Could not load the change history"),
      el("p", "", undefined, state.errorMessage),
    );
    const retry = el("button", "ch-btn-primary", "change-history-retry", "Try again");
    retry.type = "button";
    retry.addEventListener("click", () => void load());
    const acts = el("div", "ch-acts");
    acts.append(retry);
    box.append(acts);
    p.results.append(box);
    return;
  }

  const page = state.page;
  if (state.status !== "ready" || page === undefined) return;

  if (state.entries.length === 0) {
    p.results.classList.add("ch-results-empty");
    const copy = emptyCopy(state.filters, page);
    const box = el("div", "ch-empty", "change-history-empty");
    box.dataset["kind"] = copy.kind;
    box.append(el("h2", "", undefined, copy.title), el("p", "", undefined, copy.body));
    if (copy.kind === "filtered") {
      const acts = el("div", "ch-acts");
      if (filtersActive(state.filters)) {
        const clear = el(
          "button",
          "ch-btn-secondary",
          "change-history-empty-clear",
          "Clear filters",
        );
        clear.type = "button";
        clear.addEventListener("click", () => clearFilters());
        acts.append(clear);
      }
      if (state.filters.range !== "last_30_days") {
        const longer = el(
          "button",
          "ch-btn-primary",
          "change-history-empty-last30",
          "Use last 30 days",
        );
        longer.type = "button";
        longer.addEventListener("click", () => handleRangeChip("last_30_days"));
        acts.append(longer);
      }
      box.append(acts);
    }
    p.results.append(box, footer(page, false));
    return;
  }

  p.results.append(headRow());
  for (const group of groupByDay(state.entries, page.todayLocal)) {
    const day = el("div", "ch-day", "change-history-day");
    day.append(el("span", "", undefined, group.heading), el("small", "", undefined, group.sub));
    p.results.append(day);
    for (const entry of group.entries) p.results.append(renderRow(entry, state.filters.q));
  }
  p.results.append(footer(page, true));
}

function headRow(): HTMLElement {
  const head = el("div", "ch-head");
  for (const label of ["When", "Who", "Area", "What changed"])
    head.append(el("span", "", undefined, label));
  head.append(el("span"));
  return head;
}

function footer(page: ChangeHistoryPageDto, withCount: boolean): HTMLElement {
  const foot = el("div", "ch-foot", "change-history-footer");
  if (withCount) {
    foot.append(
      el("div", "", "change-history-showing", showingLine(state.entries.length, state.total)),
    );
    if (state.nextCursor !== null) {
      const more = el(
        "button",
        "ch-btn-secondary",
        "change-history-show-more",
        state.loadingMore ? "Loading..." : "Show 50 more",
      );
      more.type = "button";
      more.disabled = state.loadingMore;
      more.addEventListener("click", () => void loadMore());
      foot.append(more);
    }
  }
  const lock = el("div", "ch-lockline", "change-history-history-starts");
  lock.append(icon("lock"), document.createTextNode(historyStartsText(page)));
  foot.append(lock);
  // The areas not recorded yet, in the server's own words, so the list never claims to be complete.
  foot.append(el("p", "ch-coverage", "change-history-coverage", page.notYetRecorded));
  return foot;
}

// ---------------------------------------------------------------------------------------------
// The detail sheet
// ---------------------------------------------------------------------------------------------

function changeLabel(change: ChangeHistoryChangeDto): string {
  return change.subject === null ? change.label : `${change.subject}: ${change.label}`;
}

function renderDetail(entry: ChangeHistoryEntryDto): void {
  if (parts === undefined) return;
  const p = parts;
  p.panelEyebrow.textContent = entry.area.label;
  p.panelTitle.textContent = entry.title;
  p.panelBody.textContent = "";

  const meta = el("dl", "ch-meta");
  const add = (label: string, value: Node | string): void => {
    const dt = el("dt", "", undefined, label);
    const dd = el("dd");
    if (typeof value === "string") dd.textContent = value;
    else dd.append(value);
    meta.append(dt, dd);
  };
  add("When", entry.whenLabel);
  const who = el("span", "ch-who");
  who.append(avatar(entry, true), el("span", "", undefined, entry.actor.name));
  add("Who", who);
  add("Area", el("span", areaTagClass(entry.area.id), undefined, entry.area.label));
  for (const fact of entry.facts) add(fact.label, fact.value);
  add("Record", entry.recordShort);
  p.panelBody.append(meta);

  const listStyle =
    entry.changes.length > 1 && entry.moreCount === 0 && entry.headline.tail !== null;
  p.panelBody.append(el("div", "ch-chg-h", undefined, panelListHeading(entry).toUpperCase()));
  if (listStyle) {
    const list = el("div", "ch-mini-list", "change-history-detail-list");
    const head = el("div", "ch-mrow ch-mrow-head");
    head.append(
      el("span", "", undefined, "Item"),
      el("span", "ch-a", undefined, "Was"),
      el("span", "ch-b", undefined, "Now"),
    );
    list.append(head);
    for (const change of entry.changes) {
      const row = el("div", "ch-mrow", "change-history-detail-item");
      row.append(
        el("span", "", undefined, change.subject ?? change.label),
        el("span", "ch-a", undefined, change.hidden ? "Not shown" : (change.was ?? "")),
        el("span", "ch-b", undefined, change.hidden ? "Not shown" : (change.now ?? "")),
      );
      list.append(row);
    }
    p.panelBody.append(list);
  } else {
    for (const change of entry.changes) {
      const card = el("div", "ch-chg", "change-history-detail-change");
      card.append(el("div", "ch-fl", undefined, changeLabel(change)));
      const pair = el("div", "ch-beforeafter");
      pair.append(changeBox("Was", change, false), changeBox("Now", change, true));
      card.append(pair);
      p.panelBody.append(card);
    }
  }
  if (entry.note !== null) {
    const callout = el("div", "ch-callout", "change-history-detail-note");
    callout.append(icon("lock"), el("span", "", undefined, entry.note));
    p.panelBody.append(callout);
  }
}

function changeBox(label: string, change: ChangeHistoryChangeDto, now: boolean): HTMLElement {
  const box = el("div", `ch-ba ${change.hidden ? "ch-hidden" : now ? "ch-now" : "ch-was-box"}`);
  box.append(el("small", "", undefined, label));
  const value = el("span");
  if (change.hidden) {
    value.append(icon("lock"), document.createTextNode("Not shown"));
  } else {
    value.textContent = (now ? change.now : change.was) ?? "";
  }
  box.append(value);
  return box;
}

function openDetail(entryId: string, trigger: HTMLElement): void {
  if (parts === undefined) return;
  const entry = state.entries.find((e) => e.id === entryId);
  if (entry === undefined) return;
  state.openEntryId = entryId;
  returnFocusTo = trigger;
  renderDetail(entry);
  parts.scrim.hidden = false;
  parts.panel.hidden = false;
  for (const row of Array.from(parts.results.querySelectorAll<HTMLElement>(".ch-row"))) {
    row.classList.toggle("ch-row-sel", row.dataset["entryId"] === entryId);
  }
  parts.closeButton.focus();
}

function closeDetail(): void {
  if (parts === undefined) return;
  state.openEntryId = undefined;
  parts.scrim.hidden = true;
  parts.panel.hidden = true;
  for (const row of Array.from(parts.results.querySelectorAll<HTMLElement>(".ch-row-sel"))) {
    row.classList.remove("ch-row-sel");
  }
  const target = returnFocusTo;
  returnFocusTo = undefined;
  if (target !== undefined && target.isConnected) target.focus();
}

// ---------------------------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------------------------

function failureMessage(
  status: number,
  networkError: boolean,
  message: string | undefined,
): string {
  if (networkError || status === 0) {
    return "Could not reach the server. Check your connection and try again.";
  }
  // A server fault carries no words a person can use; a refusal of the request does.
  if (status === 503 && message === SERVICE_UNAVAILABLE_SENTENCE) {
    return SERVICE_UNAVAILABLE_SENTENCE;
  }
  if (status >= 500 || message === undefined) {
    return "Something went wrong. Try again.";
  }
  return capitalizeServerMessage(message);
}

async function load(): Promise<void> {
  const session = state.session;
  if (session === undefined || parts === undefined) return;
  state.generation += 1;
  const generation = state.generation;
  closeDetail();
  state.status = "loading";
  state.downloadNote = "";
  parts.downloadNote.textContent = "";
  syncFilterControls();
  renderSummary();
  renderResults();
  const result = await getChangeHistory(
    session.apiBaseUrl,
    session.tenantId,
    changeHistoryQuery(state.filters),
  );
  if (generation !== state.generation) return;
  if (result.status === 403) {
    state.status = "no-access";
  } else if (result.status !== 200 || result.body.entries === undefined) {
    state.status = "error";
    state.errorMessage = failureMessage(
      result.status,
      result.networkError === true,
      result.body.message,
    );
  } else {
    state.status = "ready";
    state.page = result.body;
    state.entries = result.body.entries;
    state.nextCursor = result.body.nextCursor;
    state.total = result.body.total;
    // The server resolved the range; a preset keeps its own words, a custom range keeps its dates.
    if (state.filters.range === "custom") {
      state.filters.from = result.body.from;
      state.filters.to = result.body.to;
    }
  }
  syncFilterControls();
  renderSummary();
  renderResults();
}

async function loadMore(): Promise<void> {
  const session = state.session;
  if (session === undefined || state.nextCursor === null || state.loadingMore) return;
  const generation = state.generation;
  state.loadingMore = true;
  renderResults();
  const result = await getChangeHistory(
    session.apiBaseUrl,
    session.tenantId,
    changeHistoryQuery(state.filters, { cursor: state.nextCursor }),
  );
  if (generation !== state.generation) return;
  state.loadingMore = false;
  if (result.status === 403) {
    state.status = "no-access";
  } else if (result.status !== 200 || result.body.entries === undefined) {
    state.status = "error";
    state.errorMessage = failureMessage(
      result.status,
      result.networkError === true,
      result.body.message,
    );
  } else {
    const known = new Set(state.entries.map((e) => e.id));
    state.entries = [...state.entries, ...result.body.entries.filter((e) => !known.has(e.id))];
    state.nextCursor = result.body.nextCursor;
  }
  renderSummary();
  renderResults();
}

async function handleDownload(format: GateExportFormat): Promise<void> {
  const session = state.session;
  if (session === undefined || parts === undefined || state.page === undefined) return;
  const generation = state.generation;
  parts.downloadNote.textContent = "Preparing the file...";
  parts.downloadButton.disabled = true;
  const page = state.page;
  const result = await getChangeHistoryCsv(
    session.apiBaseUrl,
    session.tenantId,
    changeHistoryQuery(state.filters, { format }),
  );
  if (generation !== state.generation || parts === undefined) return;
  parts.downloadButton.disabled = false;
  if (result.status !== 200 || result.blob === undefined) {
    parts.downloadNote.textContent = result.networkError
      ? "Could not download this file. Check your connection."
      : capitalizeServerMessage(result.message ?? "Could not download this file.");
    return;
  }
  await deliverExportFile(
    parts.downloadNote,
    result.blob,
    `change-history-${page.from}-to-${page.to}`,
    format,
  );
}

// ---------------------------------------------------------------------------------------------
// Entry points for ui/src/admin.ts
// ---------------------------------------------------------------------------------------------

/** Opens the screen inside `container` on the default range with no filters. */
export async function openChangeHistory(session: Session, container: HTMLElement): Promise<void> {
  resetChangeHistory();
  state.session = session;
  state.filters = defaultFilters();
  parts = buildOnce(container);
  await load();
}

/** A fresh permission snapshot arrived: nothing is reloaded, the server decides on the next read. */
export function refreshChangeHistorySession(session: Session): void {
  state.session = session;
}

/** Sign-out and leaving the screen: forget the list (it is one person's view of the whole location's
 * history) and close the sheet, so the next person on a shared tablet sees nothing of it. */
export function resetChangeHistory(): void {
  state.generation += 1;
  if (state.searchTimer !== undefined) clearTimeout(state.searchTimer);
  state.searchTimer = undefined;
  state.session = undefined;
  state.filters = defaultFilters();
  state.status = "idle";
  state.errorMessage = "";
  state.page = undefined;
  state.entries = [];
  state.nextCursor = null;
  state.total = null;
  state.loadingMore = false;
  state.openEntryId = undefined;
  state.downloadNote = "";
  returnFocusTo = undefined;
  if (parts !== undefined) {
    parts.scrim.hidden = true;
    parts.panel.hidden = true;
    parts.root.remove();
    parts = undefined;
  }
}

/** Closes the detail sheet if it is open (called by the admin teardown on sign-out). */
export function dismissChangeHistorySheet(): void {
  if (parts !== undefined) closeDetail();
}

export function changeHistorySheetOpen(): boolean {
  return parts !== undefined && !parts.panel.hidden;
}
